import React from 'react';
import { cn } from '@/utils';
import { ITableColumn } from './Table.types';

interface TableCellProps {
  /** Column definition */
  column: ITableColumn;
  /** Cell content value */
  value: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * TableCell Component
 *
 * Renders a table cell with flexible content support.
 * Handles alignment, width, and styling based on column configuration.
 */
export const TableCell: React.FC<TableCellProps> = ({
  column,
  value,
  className,
}) => {
  const getWidth = () => {
    if (column.width) {
      return typeof column.width === 'number' 
        ? `${column.width}px` 
        : column.width;
    }

    return 'auto';
  };

  return (
    <td
      className={cn(
        'px-4 py-2 h-[72px] align-middle',
        column.align === 'center' && 'text-center',
        column.align === 'right' && 'text-right',
        column.columnClassName,
        className
      )}
      style={column.width ? { width: getWidth(), minWidth: getWidth() } : { minWidth: 'max-content' }}
    >
      <div className={cn(
        'flex items-center gap-2',
        column.align === 'center' && 'justify-center',
        column.align === 'right' && 'justify-end'
      )}>
        {value}
      </div>
    </td>
  );
};

TableCell.displayName = 'TableCell';